// components/Enrollment/StudentCourseDetail.js
// Student-facing course detail + batch selection + apply/pay
// Uses self-paced detail page styling for visual consistency
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Modal } from 'react-bootstrap';
import {
  Clock, Layers, Users, Calendar, CheckCircle2, MapPin,
  Info, ShieldCheck, CreditCard, ArrowRight, AlertCircle, XCircle,
  Play,
} from 'lucide-react';
import { AppButton, AppBadge, BackButton, EmptyState, Tabs, TabsList, TabsTrigger, TabsContent } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import { getCatalogCourseDetail, applyToCourse, verifyEnrollmentPayment } from '../../utils/enrollmentApi';
import apiClient from '../../utils/apiClient';
import useRazorpay from '../../hooks/useRazorpay';
import DOMPurify from 'dompurify';
import '../SelfPaced/_self-paced-pages.scss';
import './_enrollment-pages.scss';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// Parse newline-separated strings into arrays
const toList = (v) => (Array.isArray(v) ? v : typeof v === 'string' ? v.split('\n').map(s => s.trim()).filter(Boolean) : []);
const isHtml = (s) => typeof s === 'string' && /<[a-z][\s\S]*>/i.test(s);

export default function StudentCourseDetail() {
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const [course, setCourse] = useState(null);
  const [batches, setBatches] = useState([]);
  const [existingRequest, setExistingRequest] = useState(null);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [previewVideos, setPreviewVideos] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamLoading, setStreamLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCatalogCourseDetail(courseId);
      const d = res.data?.data || res.data;
      setCourse(d.course);
      setBatches(d.batches || []);
      setExistingRequest(d.existingRequest || null);
      setPaymentEnabled(!!d.paymentEnabled);
      // Auto-select first selectable batch
      const selectable = (d.batches || []).filter(b => b.status === 'Upcoming' || b.status === 'Ongoing');
      if (selectable.length === 1) setSelectedBatch(selectable[0]._id);

      // Fetch preview videos (public endpoint, no auth required)
      try {
        const pvRes = await apiClient.get(`/materials/course/${courseId}/previews`);
        if (pvRes.data?.success) setPreviewVideos(pvRes.data.data || []);
      } catch {}
    } catch {
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  // Use feeAmount from backend (pre-parsed), fallback to parseFloat
  const feeAmount = course ? (course.feeAmount != null ? course.feeAmount : (parseFloat(String(course.fees || '0').replace(/[^0-9.]/g, '')) || 0)) : 0;
  const isFree = feeAmount === 0;

  // Filter batches: only show if endDate is today or future (status field may be stale)
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const selectableBatches = batches.filter(b =>
    (b.status === 'Upcoming' || b.status === 'Ongoing') &&
    (!b.endDate || new Date(b.endDate) >= now)
  );
  const hasSelectableBatches = selectableBatches.length > 0;

  // Parse text content
  const highlights = toList(course?.highlights);
  const prerequisites = toList(course?.prerequisites);
  const syllabus = toList(course?.syllabus);

  const handleApply = async () => {
    if (!course) return;
    if (hasSelectableBatches && !selectedBatch) {
      setToast({ show: true, message: 'Please select a batch first.', type: 'error' });
      return;
    }

    setApplying(true);
    try {
      const res = await applyToCourse({
        courseId: course._id,
        batchId: selectedBatch || null,
      });
      const data = res.data?.data || res.data;

      // Free course or no payment gateway → request submitted
      if (isFree || !data.orderId) {
        setToast({ show: true, message: res.data?.message || 'Request submitted!', type: 'success' });
        setExistingRequest(data.request || data);
        setApplying(false);
        return;
      }

      // Paid → open Razorpay
      openCheckout(
        {
          razorpayKeyId: data.razorpayKeyId,
          order: { id: data.orderId, amount: Math.round(data.amount * 100), currency: data.currency || 'INR' },
          description: `Course enrollment: ${course.name || course.title}`,
        },
        async (response) => {
          try {
            await verifyEnrollmentPayment({
              courseId: course._id,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            setToast({ show: true, message: 'Payment successful! You are now enrolled.', type: 'success' });
            setExistingRequest({ status: 'paid' });
          } catch {
            setToast({ show: true, message: 'Payment verification failed.', type: 'error' });
          }
          setApplying(false);
        },
        (err) => {
          setToast({ show: true, message: err?.reason || 'Payment cancelled.', type: 'error' });
          setApplying(false);
        },
      );
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to apply.', type: 'error' });
      setApplying(false);
    }
  };

  /* Status banner config */
  const STATUS_CONFIG = {
    pending: {
      icon: <Clock size={16} />,
      label: existingRequest?.batchId
        ? 'Your request is pending admin approval.'
        : 'Application received! You\'ll be assigned to the next available batch.',
    },
    approved: {
      icon: <CheckCircle2 size={16} />,
      label: existingRequest?.alreadyEnrolled
        ? 'You are already enrolled in this course.'
        : 'You are enrolled in this course!',
    },
    paid: { icon: <CheckCircle2 size={16} />, label: 'Payment received — you are enrolled!' },
    rejected: { icon: <XCircle size={16} />, label: 'Your request was rejected.' },
  };

  const isAlreadyEnrolled = !!(existingRequest?.alreadyEnrolled);

  /* CTA button label */
  const getCtaLabel = () => {
    if (applying || rzpLoading) return <><Spinner size="sm" animation="border" /> Processing…</>;
    if (isFree) return hasSelectableBatches ? 'Enroll Now — Free' : 'Apply for this Course — Free';
    if (!paymentEnabled) return hasSelectableBatches ? 'Apply for Enrollment' : 'Apply for this Course';
    return hasSelectableBatches
      ? <>Pay ₹{feeAmount.toLocaleString('en-IN')} & Enroll <ArrowRight size={15} /></>
      : <>Apply — ₹{feeAmount.toLocaleString('en-IN')} <ArrowRight size={15} /></>;
  };

  if (loading) return <div className="sp-page"><div className="sp-loading"><Spinner animation="border" variant="secondary" /></div></div>;
  if (!course) return <div className="sp-page"><EmptyState title="Course not found" description="This course may no longer be available." /></div>;

  const statusCfg = existingRequest ? STATUS_CONFIG[existingRequest.status] : null;

  const formatDuration = (min) => {
    if (!min || min <= 0) return null;
    if (min >= 60) return `${Math.floor(min / 60)}h ${Math.round(min % 60)}m`;
    return `${Math.round(min)}m`;
  };

  const handlePlayPreview = async (video) => {
    setPlayingVideo(video);
    setStreamUrl(null);
    setStreamLoading(true);
    try {
      const res = await apiClient.get(`/materials/${video._id}/stream`);
      if (res.data?.success) setStreamUrl(res.data.data.url);
    } catch {
      setToast({ show: true, message: 'Failed to load video.', type: 'error' });
      setPlayingVideo(null);
    }
    setStreamLoading(false);
  };

  return (
    <div className="sp-page">
      {/* Banner */}
      <div className="spd-banner">
        <div className="spd-banner__main">
          <div style={{ marginBottom: '0.5rem' }}>
            <BackButton onClick={() => navigate('/s/courses')} />
          </div>
          {course.shortCode && <AppBadge variant="secondary" color="primary">{course.shortCode}</AppBadge>}
          <h1 className="spd-banner__title">{course.name}</h1>
          {course.description && <p className="spd-banner__desc">{course.description}</p>}

          <div className="spd-banner__meta">
            {course.duration && <span><Clock size={14} /> {course.duration}</span>}
            {course.mode && <span><Layers size={14} /> {course.mode}</span>}
            {hasSelectableBatches && <span><Calendar size={14} /> {selectableBatches.length} batch{selectableBatches.length !== 1 ? 'es' : ''} available</span>}
          </div>
        </div>

        {/* ── Sidebar Card ───────────────────────────────────── */}
        <div className="spd-sidebar">
          <div className="spd-sidebar__card">
            {(course.thumbnailUrl || course.thumbnail || course.image) && (
              <div className="spd-sidebar__thumb">
                <img src={course.thumbnailUrl || course.thumbnail || course.image} alt={course.name} />
              </div>
            )}

            <div className="spd-sidebar__body">
              {/* Pricing */}
              <div className="spd-sidebar__pricing">
                {isAlreadyEnrolled || existingRequest?.status === 'paid' || existingRequest?.status === 'approved' ? (
                  <AppBadge color="emerald" size="sm">✓ Enrolled</AppBadge>
                ) : isFree ? (
                  <span className="spd-sidebar__price spd-sidebar__price--free">Free</span>
                ) : (
                  <span className="spd-sidebar__price">₹{feeAmount.toLocaleString('en-IN')}</span>
                )}
              </div>

              {/* Status banner */}
              {statusCfg && (
                <div className={`enr-detail__status enr-detail__status--${existingRequest.status}`}>
                  {statusCfg.icon}
                  <span>{statusCfg.label}</span>
                </div>
              )}

              {/* No batch info — hide when already enrolled */}
              {!existingRequest && !isAlreadyEnrolled && !hasSelectableBatches && (
                <div className="enr-detail__status enr-detail__status--info">
                  <Info size={15} />
                  <span>No upcoming batches yet. Apply now — you'll be assigned when the next batch starts.</span>
                </div>
              )}

              {/* Payment info for paid courses — hide when already enrolled */}
              {!existingRequest && !isAlreadyEnrolled && !isFree && (
                <div style={{ display: 'flex' }}>
                  {paymentEnabled ? (
                    <span className="enr-detail__payment-tag enr-detail__payment-tag--online">
                      <CreditCard size={13} /> Secure online payment
                    </span>
                  ) : (
                    <span className="enr-detail__payment-tag enr-detail__payment-tag--manual">
                      <AlertCircle size={13} /> Admin approval required
                    </span>
                  )}
                </div>
              )}

              {/* CTA Button — hide when already enrolled */}
              {!isAlreadyEnrolled && (!existingRequest || existingRequest.status === 'rejected') ? (
                <AppButton block onClick={handleApply} disabled={applying || rzpLoading}>
                  {getCtaLabel()}
                </AppButton>
              ) : null}

              {/* Perks */}
              <ul className="spd-sidebar__perks">
                <li><CheckCircle2 size={14} /> Instructor-led batch training</li>
                <li><ShieldCheck size={14} /> Structured curriculum</li>
                {course.duration && <li><Clock size={14} /> Duration: {course.duration}</li>}
                {course.mode && <li><MapPin size={14} /> Mode: {course.mode}</li>}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ── Batch Selection ─────────────────────────────────── */}
      {hasSelectableBatches && !existingRequest && !isAlreadyEnrolled && (
        <section className="spd-section">
          <h3 className="spd-section__title">
            <Calendar size={18} /> Choose a Batch
          </h3>
          <div className="enr-batches__list">
            {selectableBatches.map(b => {
              const seatsLeft = b.maxStudents ? Math.max(b.maxStudents - (b.strength || 0), 0) : null;
              const isSelected = selectedBatch === b._id;
              const isFull = seatsLeft === 0;

              return (
                <div
                  key={b._id}
                  className={`enr-batch-card${isSelected ? ' enr-batch-card--selected' : ''}${isFull ? ' enr-batch-card--full' : ''}`}
                  onClick={() => !isFull && setSelectedBatch(b._id)}
                >
                  <div className="enr-batch-card__radio">
                    <div className={`enr-batch-card__radio-dot${isSelected ? ' enr-batch-card__radio-dot--active' : ''}`} />
                  </div>
                  <div className="enr-batch-card__info">
                    <div className="enr-batch-card__name">
                      {b.name}
                      <AppBadge color={b.status === 'Upcoming' ? 'primary' : 'emerald'} size="sm">{b.status}</AppBadge>
                    </div>
                    <div className="enr-batch-card__meta">
                      <span><Calendar size={13} /> {formatDate(b.startDate)} — {formatDate(b.endDate)}</span>
                      {b.startTime && <span><Clock size={13} /> {b.startTime}</span>}
                      {b.mode && <span><MapPin size={13} /> {b.mode}</span>}
                      {b.batchType && <span><Layers size={13} /> {b.batchType}</span>}
                    </div>
                  </div>
                  <div className="enr-batch-card__seats">
                    {isFull ? (
                      <AppBadge color="danger" size="sm">Full</AppBadge>
                    ) : seatsLeft !== null ? (
                      <span className="enr-batch-card__seats-text">
                        <Users size={13} /> {seatsLeft} seat{seatsLeft !== 1 ? 's' : ''} left
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Tabbed Content ──────────────────────────────────── */}
      {(() => {
        const hasSyllabus = syllabus.length > 0 || isHtml(course?.syllabus);
        const hasPrereqs = prerequisites.length > 0 || isHtml(course?.prerequisites);
        const hasHighlights = highlights.length > 0 || isHtml(course?.highlights);
        const hasVideos = previewVideos.length > 0;
        if (!hasSyllabus && !hasPrereqs && !hasHighlights && !hasVideos) return null;
        const defaultTab = hasSyllabus ? 'syllabus' : hasPrereqs ? 'prerequisites' : hasHighlights ? 'highlights' : 'videos';
        return (
          <section className="spd-section">
            <Tabs defaultValue={defaultTab}>
              <TabsList>
                {hasSyllabus && <TabsTrigger value="syllabus">Syllabus</TabsTrigger>}
                {hasPrereqs && <TabsTrigger value="prerequisites">Prerequisites</TabsTrigger>}
                {hasHighlights && <TabsTrigger value="highlights">What you'll learn</TabsTrigger>}
                {hasVideos && <TabsTrigger value="videos">Free Videos</TabsTrigger>}
              </TabsList>

              {hasSyllabus && (
                <TabsContent value="syllabus">
                  {isHtml(course?.syllabus) ? (
                    <div className="spd-prerequisites spd-prerequisites--html ql-editor" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course.syllabus) }} />
                  ) : (
                    <ul className="spd-prerequisites">
                      {syllabus.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                </TabsContent>
              )}

              {hasPrereqs && (
                <TabsContent value="prerequisites">
                  {isHtml(course?.prerequisites) ? (
                    <div className="spd-prerequisites spd-prerequisites--html ql-editor" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course.prerequisites) }} />
                  ) : (
                    <ul className="spd-prerequisites">
                      {prerequisites.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                </TabsContent>
              )}

              {hasHighlights && (
                <TabsContent value="highlights">
                  {isHtml(course?.highlights) ? (
                    <div className="spd-prerequisites spd-prerequisites--html ql-editor" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course.highlights) }} />
                  ) : (
                    <ul className="spd-prerequisites">
                      {highlights.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  )}
                </TabsContent>
              )}

              {hasVideos && (
                <TabsContent value="videos">
                  <div className="enr-preview-videos">
                    {previewVideos.map((v) => (
                      <div key={v._id} className="enr-preview-card" onClick={() => handlePlayPreview(v)}>
                        <div className="enr-preview-card__thumb">
                          {v.thumbnailUrl ? <img src={v.thumbnailUrl} alt="" /> : <div className="enr-preview-card__thumb-placeholder"><Play size={28} /></div>}
                          <div className="enr-preview-card__play-overlay"><Play size={24} /></div>
                          {formatDuration(v.duration) && (
                            <span className="enr-preview-card__duration">{formatDuration(v.duration)}</span>
                          )}
                        </div>
                        <div className="enr-preview-card__info">
                          <h4 className="enr-preview-card__title">{v.title}</h4>
                          {v.description && <p className="enr-preview-card__desc">{v.description}</p>}
                          <AppBadge color="emerald" size="sm">Free Preview</AppBadge>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </section>
        );
      })()}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* ── Preview Video Player Modal ──────────────────────── */}
      <Modal
        show={!!playingVideo}
        onHide={() => { setPlayingVideo(null); setStreamUrl(null); }}
        centered
        size="lg"
        className="spd-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-semibold">{playingVideo?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {streamLoading ? (
            <div className="spd-preview-modal__loading"><Spinner animation="border" variant="secondary" /></div>
          ) : streamUrl ? (
            <video
              src={streamUrl}
              controls
              autoPlay
              preload="auto"
              className="spd-preview-modal__player"
            />
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
}
